import {
  SigningRefusalError,
  SigningRequestError,
  type SigningRefusalReason,
} from "@/domains/documents/signing/repository";
import type { SigningTranslations } from "@/lib/i18n/types/signing";

/**
 * What to show the user for a failed `dokobit-sign-start` / `-open` / `-cancel`.
 *
 * EVERY branch returns translated copy. The edge functions answer in English
 * only — a `reason` where they have one, a hand-written English sentence where
 * they don't — so surfacing a backend message verbatim puts untranslated
 * developer prose ("signers: the same person cannot be invited twice") in front
 * of an Estonian user. A refusal `reason` is the precise mapping; failing that,
 * the HTTP status still separates the two cases worth separating — "somebody
 * else's signing is holding these documents" (409) and "you may not do this"
 * (403) — from a generic rejection.
 *
 * `fallback` is for the errors that are not the backend refusing at all: a
 * network failure, or a function that isn't deployed yet answering 404 with a
 * body that isn't ours. It is caller-supplied because that phrasing is
 * context-specific ("Signing failed" vs "Could not load signatures").
 */
export function refusalMessage(
  error: unknown,
  copy: SigningTranslations,
  fallback: string
): string {
  if (error instanceof SigningRefusalError) {
    const key = REFUSAL_KEY[error.reason];
    return key ? copy.refusal[key] : copy.refusal.requestRejected;
  }
  if (error instanceof SigningRequestError) {
    return copy.refusal[STATUS_KEY[error.status] ?? "requestRejected"];
  }
  return fallback;
}

/**
 * Statuses worth their own sentence. 409 is the in-flight-chain collision the
 * user can clear by refreshing; 403 is a permission answer no retry changes.
 * Everything else (400 validation, 404, 500) is one generic rejection — there
 * is no client-readable signal to say more, and guessing from English message
 * text would break the moment the backend rewords a sentence.
 */
const STATUS_KEY: Record<number, keyof SigningTranslations["refusal"]> = {
  403: "notAllowed",
  409: "alreadyInProgress",
};

const REFUSAL_KEY: Record<SigningRefusalReason, keyof SigningTranslations["refusal"]> = {
  unknown_link: "unknownLink",
  chain_closed: "chainClosed",
  already_signed: "alreadySigned",
  not_your_turn: "notYourTurn",
  no_longer_a_member: "noLongerAMember",
  chain_differs: "chainDiffers",
  finalizing: "finalizing",
  fully_signed: "fullySigned",
};
